import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Users, Search, Download, RefreshCw, Loader2,
  IdCard, ShieldCheck, Mail, KeyRound, UserPlus, Building2, Layers, Wifi, Globe,
} from 'lucide-react';
import { toast } from 'sonner';
import { FullPagination } from '../../components/admin/FullPagination';
import { ConfirmActionDialog } from '../../components/admin/ConfirmActionDialog';
import { ClientCardModal } from '../../components/admin/ClientCardModal';
import { ChangeEmailCard, ResetCredentialsCard, CreateAccountCard } from '../../components/admin/ClientAccountCards';
import { adminClientService, AdminClient, AdminClientSourceFilter } from '../../services/adminClientService';
import { provinciaToRegione } from '../../utils/provinceToRegione';
import { ClientActionsMenu, ClientActionsMenuItem } from '../../components/admin/ClientActionsMenu';
import api from '../../services/api';

const STATUS_COLOR: Record<string, string> = {
  attivo: 'bg-emerald-100 text-emerald-700',
  sospeso: 'bg-amber-100 text-amber-700',
  bloccato: 'bg-red-100 text-red-700',
};

const SOURCE_FILTERS: { value: AdminClientSourceFilter; label: string; icon: React.ReactNode }[] = [
  { value: 'all', label: 'Tutti', icon: <Layers className="w-3.5 h-3.5" /> },
  { value: 'gestionale', label: 'Solo Gestionale', icon: <Building2 className="w-3.5 h-3.5" /> },
  { value: 'both', label: 'Entrambi', icon: <ShieldCheck className="w-3.5 h-3.5" /> },
  // I Contatti: approvati dal sito, il cliente NTS nasce al primo ordine.
  { value: 'web', label: 'Solo Web', icon: <Globe className="w-3.5 h-3.5" /> },
  // Quinto valore dello stesso gruppo, non un interruttore a parte:
  // incrociare la presenza con l'origine anagrafica non risponde a nessuna
  // domanda reale. È il bersaglio della card "Online ora" della dashboard.
  { value: 'online', label: 'Online ora', icon: <Wifi className="w-3.5 h-3.5" /> },
];

const VALID_SOURCES = SOURCE_FILTERS.map(f => f.value);

const PAGE_SIZE = 50;

/** "2026-09-08T11:08:07" → "2 min". Quanto tempo è passato dall'ultima
 * azione: su una lista di chi è online adesso l'orario assoluto è meno
 * leggibile della distanza. */
function fmtDaQuanto(iso?: string | null): string {
  if (!iso) return '—';
  const at = new Date(iso.endsWith('Z') || /[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`).getTime();
  if (Number.isNaN(at)) return '—';
  const minuti = Math.max(0, Math.round((Date.now() - at) / 60000));
  if (minuti < 1) return 'adesso';
  return `${minuti} min`;
}

type PendingAction = { type: 'block' | 'unblock'; clientId: string; clientName: string } | null;
type ActionModal = { type: 'change-email' | 'reset-credentials' | 'create-account'; client: AdminClient } | null;

function fmtDate(iso?: string | null) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }); }
  catch { return '—'; }
}

/** Dove porta la riga: la scheda Cliente, o per un Contatto (senza
 *  ``an_conto``) la scheda della sua Registrazione. */
function schedaDi(c: AdminClient): string {
  return c.source === 'web' ? `/admin/registrazioni/${c.userId}` : `/admin/clienti/${c.an_conto}`;
}

/** Chip di un Contatto, al posto del codice cliente che ancora non ha. */
function ChipWeb({ c }: { c: AdminClient }) {
  return (
    <span
      className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 border border-sky-200 font-semibold font-sans"
      title="Registrato dal sito: il cliente sul gestionale nasce con il primo ordine"
      data-testid={`client-web-chip-${c.id}`}
    >
      Web
    </span>
  );
}

function renderClientActions(c: AdminClient, onOpenModal: (m: ActionModal) => void): React.ReactElement {
  if (c.source === 'web') {
    // Un Contatto ha l'account ma non il conto: le azioni account vanno per
    // ``userId`` (il backend le accetta), la scheda è la Registrazione.
    return (
      <ClientActionsMenu testId={`client-actions-menu-${c.id}`}>
        <ClientActionsMenuItem to={schedaDi(c)} testId={`client-menu-scheda-${c.id}`}>
          <IdCard className="w-4 h-4" /> Scheda Registrazione
        </ClientActionsMenuItem>
        <ClientActionsMenuItem onClick={() => onOpenModal({ type: 'change-email', client: c })} testId={`client-menu-cambio-mail-${c.id}`}>
          <Mail className="w-4 h-4" /> Cambio Mail
        </ClientActionsMenuItem>
        <ClientActionsMenuItem onClick={() => onOpenModal({ type: 'reset-credentials', client: c })} testId={`client-menu-reset-credenziali-${c.id}`}>
          <KeyRound className="w-4 h-4" /> Reset Credenziali
        </ClientActionsMenuItem>
      </ClientActionsMenu>
    );
  }
  const menuItems: React.ReactElement[] = [
    <ClientActionsMenuItem key="scheda" to={`/admin/clienti/${c.an_conto}`} testId={`client-menu-scheda-${c.id}`}>
      <IdCard className="w-4 h-4" /> Scheda Cliente
    </ClientActionsMenuItem>,
  ];
  if (c.userId) {
    menuItems.push(
      <ClientActionsMenuItem key="accessi" to={`/admin/clienti/${c.an_conto}/accessi`} testId={`client-menu-accessi-${c.id}`}>
        <ShieldCheck className="w-4 h-4" /> Dettaglio Accessi
      </ClientActionsMenuItem>,
      <ClientActionsMenuItem key="cambio-mail" onClick={() => onOpenModal({ type: 'change-email', client: c })} testId={`client-menu-cambio-mail-${c.id}`}>
        <Mail className="w-4 h-4" /> Cambio Mail
      </ClientActionsMenuItem>,
      <ClientActionsMenuItem key="reset-cred" onClick={() => onOpenModal({ type: 'reset-credentials', client: c })} testId={`client-menu-reset-credenziali-${c.id}`}>
        <KeyRound className="w-4 h-4" /> Reset Credenziali
      </ClientActionsMenuItem>,
    );
  } else {
    menuItems.push(
      <ClientActionsMenuItem key="crea-utenza" onClick={() => onOpenModal({ type: 'create-account', client: c })} testId={`client-menu-crea-utenza-${c.id}`}>
        <UserPlus className="w-4 h-4" /> Crea Utenza
      </ClientActionsMenuItem>,

    );
  }
  return <ClientActionsMenu testId={`client-actions-menu-${c.id}`}>{menuItems}</ClientActionsMenu>;
}

export default function AdminClientsPage() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  // Il filtro nasce dalla query string così la card "Online ora" della
  // dashboard può linkare direttamente ``/admin/clienti?source=online``.
  // Sincronizzato nell'URL a ogni cambio: il link resta condivisibile e il
  // tasto Indietro torna al filtro precedente invece che alla dashboard.
  const [searchParams, setSearchParams] = useSearchParams();
  const sourceFromUrl = searchParams.get('source') as AdminClientSourceFilter | null;
  const sourceFilter: AdminClientSourceFilter =
    sourceFromUrl && VALID_SOURCES.includes(sourceFromUrl) ? sourceFromUrl : 'all';
  const setSourceFilter = (value: AdminClientSourceFilter) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('source');
    else next.set('source', value);
    setSearchParams(next, { replace: true });
  };
  // La colonna "Ultima azione" esiste solo in questa vista: è l'unica in
  // cui il backend popola ``lastActivityAt``.
  const isOnlineView = sourceFilter === 'online';
  const [page, setPage] = useState(1);
  const [pendingAction, setPendingAction] = useState<PendingAction>(null);
  const [actionModal, setActionModal] = useState<ActionModal>(null);
  const [items, setItems] = useState<AdminClient[]>([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // Debounce search input (300ms)
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => { setPage(1); }, [debouncedSearch, sourceFilter]);

  // Fetch from backend
  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    adminClientService
      .list({ q: debouncedSearch || undefined, page, limit: PAGE_SIZE, source: sourceFilter })
      .then((res) => {
        if (cancelled) return;
        setItems(res.items);
        setTotal(res.total);
        setPages(res.pages);
      })
      .catch((err: any) => {
        if (cancelled) return;
        const status = err?.response?.status;
        setError(
          status === 403
            ? 'Accesso negato — ruolo admin richiesto.'
            : 'Impossibile caricare le anagrafiche dal server.'
        );
        setItems([]);
        setTotal(0);
      })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [debouncedSearch, page, sourceFilter, reloadKey]);

  const [exporting, setExporting] = useState<'filtered' | 'all' | null>(null);

  const handleExport = async (all: boolean) => {
    setExporting(all ? 'all' : 'filtered');
    try {
      const params: Record<string, string> = {};
      if (!all && debouncedSearch) params.q = debouncedSearch;
      const qs = new URLSearchParams(params).toString();
      const url = `/api/admin/clients/export-csv${qs ? '?' + qs : ''}`;
      const res = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = all ? 'clienti_tutti.csv' : 'clienti_filtrati.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(href);
      toast.success(all ? 'Export tutti i clienti avviato' : 'Export clienti filtrati avviato');
    } catch {
      toast.error("Errore durante l'export CSV");
    } finally {
      setExporting(null);
    }
  };

  const handleConfirmAction = () => {
    if (!pendingAction) return;
    toast.info(
      `Per ${pendingAction.type === 'block' ? 'bloccare' : 'sbloccare'} ${pendingAction.clientName} agire direttamente sul gestionale ERP (campo an_blocco).`
    );
    setPendingAction(null);
  };

  const visibleRange = useMemo(() => {
    if (!total) return '0–0';
    const start = (page - 1) * PAGE_SIZE + 1;
    const end = Math.min(total, page * PAGE_SIZE);
    return `${start}–${end}`;
  }, [page, total]);

  const closeModalAndReload = () => {
    setActionModal(null);
    setReloadKey(k => k + 1);
  };

  return (
    <div className="p-4 sm:p-6" data-testid="admin-clients-page">
      <div className="flex items-center justify-between mb-4 gap-4 flex-wrap">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight flex items-center gap-2">
            <Users className="w-5 h-5 sm:w-6 sm:h-6 text-[#2B5EA7]" /> Clienti & Anagrafiche ERP
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
            Anagrafiche reali sincronizzate dal gestionale DTKNTS.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => handleExport(false)}
            disabled={exporting !== null || !total}
            data-testid="clients-export-filtered-btn"
            className="h-9 px-3 rounded-lg border text-sm font-medium flex items-center gap-1.5 hover:bg-secondary/60 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            title={debouncedSearch ? `Esporta ${total} clienti filtrati in CSV` : 'Esporta clienti filtrati in CSV'}
          >
            {exporting === 'filtered' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">Esporta filtrati</span>
            <span className="sm:hidden">Filtrati</span>
          </button>
          <button
            onClick={() => handleExport(true)}
            disabled={exporting !== null}
            data-testid="clients-export-all-btn"
            className="h-9 px-3 rounded-lg border text-sm font-medium flex items-center gap-1.5 bg-[#2B5EA7] text-white hover:bg-[#1E4D8C] border-transparent transition-colors disabled:opacity-50"
            title="Esporta tutti i clienti in CSV (nessun filtro)"
          >
            {exporting === 'all' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">Esporta tutti</span>
            <span className="sm:hidden">Tutti</span>
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="relative max-w-md flex-1 min-w-[250px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per ragione sociale, P.IVA, C.F., città…"
            data-testid="clients-search"
            className="w-full h-9 pl-9 pr-3 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-[#2B5EA7]/30"
          />
        </div>
        <span className="text-xs text-muted-foreground" data-testid="clients-counter">
          {isLoading ? 'Caricamento…' : `${total} clienti · mostrando ${visibleRange}`}
        </span>
        {isLoading && <RefreshCw className="w-3.5 h-3.5 text-muted-foreground animate-spin" />}
      </div>

      <div className="flex items-center gap-1.5 mb-4 flex-wrap" data-testid="clients-source-filter" role="radiogroup" aria-label="Filtro origine cliente">
        {SOURCE_FILTERS.map((f) => (
          <button
            key={f.value}
            role="radio"
            aria-checked={sourceFilter === f.value}
            onClick={() => setSourceFilter(f.value)}
            data-testid={`clients-source-filter-${f.value}`}
            className={`h-8 px-3 rounded-full text-xs font-medium flex items-center gap-1.5 border transition-colors ${
              sourceFilter === f.value
                ? 'bg-[#2B5EA7] text-white border-[#2B5EA7]'
                : 'bg-white text-muted-foreground border-gray-200 hover:bg-gray-50 hover:text-foreground'
            }`}
          >
            {f.icon} {f.label}
          </button>
        ))}
      </div>

      {error ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center text-sm text-red-700" data-testid="clients-error">
          {error}
        </div>
      ) : (
      <div className="bg-white rounded-xl border overflow-hidden">
        {items.length === 0 && !isLoading ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground" data-testid="clients-empty">
            {isOnlineView ? <Wifi className="w-10 h-10 mb-3 opacity-30" /> : <Users className="w-10 h-10 mb-3 opacity-30" />}
            {/* Una lista vuota deve dire perché è vuota: qui "nessun cliente
                trovato" farebbe sospettare un guasto invece di un portale
                momentaneamente senza nessuno sopra. */}
            <p className="font-medium">
              {isOnlineView ? 'Nessun cliente online in questo momento.' : 'Nessun cliente trovato.'}
            </p>
          </div>
        ) : (
          <div className="overflow-auto max-h-[65vh] table-scroll-visible" data-testid="clients-table-scroll">
            <table className="w-full" style={{ minWidth: 1400 }}>
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap sticky left-0 top-0 z-30 bg-gray-50 border-r border-gray-200" style={{ width: 110 }}>Cod.Cli</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap sticky top-0 z-20 bg-gray-50" style={{ width: 220 }}>Ragione Sociale</th>
                  {isOnlineView && (
                    <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap sticky top-0 z-20 bg-gray-50">Ultima Azione</th>
                  )}
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap sticky top-0 z-20 bg-gray-50">Partita Iva</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap sticky top-0 z-20 bg-gray-50">Email</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap sticky top-0 z-20 bg-gray-50">Telefono</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap sticky top-0 z-20 bg-gray-50">Indirizzo</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap sticky top-0 z-20 bg-gray-50">Città</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap sticky top-0 z-20 bg-gray-50">Provincia</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap sticky top-0 z-20 bg-gray-50">Regione</th>
                  <th className="text-left px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap sticky top-0 z-20 bg-gray-50">Data Reset Password</th>
                  <th className="text-right px-4 py-3 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide w-14 sticky right-0 top-0 z-30 bg-gray-50 border-l border-gray-200" />
                </tr>
              </thead>
              <tbody>
                {items.map((c) => (
                  <tr
                    key={c.id}
                    className="border-b last:border-b-0 hover:bg-gray-50/60 transition-colors group"
                    data-testid={`client-row-${c.id}`}
                  >
                    <td className="px-4 py-3 text-sm font-mono font-semibold text-[#2B5EA7] whitespace-nowrap sticky left-0 z-10 bg-white group-hover:bg-gray-50 border-r border-gray-200 relative after:content-[''] after:absolute after:top-0 after:bottom-0 after:right-0 after:translate-x-full after:w-2 after:pointer-events-none after:[background:linear-gradient(to_right,rgba(0,0,0,0.10),rgba(0,0,0,0))]" style={{ width: 110 }}>
                      {c.source === 'web' ? (
                        <Link to={schedaDi(c)} data-testid={`client-code-link-${c.id}`}><ChipWeb c={c} /></Link>
                      ) : (
                        <Link to={schedaDi(c)} className="hover:underline" data-testid={`client-code-link-${c.id}`}>
                          {c.id}
                        </Link>
                      )}
                    </td>
                    <td className="px-4 py-3" style={{ width: 220 }}>
                      <div className="flex items-center gap-2">
                        <Link
                          to={schedaDi(c)}
                          className="text-sm font-medium hover:underline hover:text-[#2B5EA7] transition-colors whitespace-nowrap"
                          data-testid={`client-name-link-${c.id}`}
                        >
                          {c.company}
                        </Link>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium flex-shrink-0 ${STATUS_COLOR[c.status]}`}>{c.status}</span>
                        {(c.seed || c.budgetSeed) && (
                          <span
                            className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 border border-amber-200 font-semibold flex-shrink-0"
                            title={c.seedLabel || 'Cliente di test'}
                            data-testid={`client-seed-badge-${c.id}`}
                          >
                            SEED
                          </span>
                        )}
                      </div>
                    </td>
                    {isOnlineView && (
                      <td className="px-4 py-3 text-sm whitespace-nowrap" data-testid={`client-last-activity-${c.id}`}>
                        <span className="inline-flex items-center gap-1.5 text-emerald-700 font-medium">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
                          {fmtDaQuanto(c.lastActivityAt)}
                        </span>
                      </td>
                    )}
                    <td className="px-4 py-3 text-sm font-mono text-muted-foreground whitespace-nowrap">{c.vat || '—'}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{c.email || '—'}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{c.phone || c.mobile || '—'}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{c.address || '—'}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{c.city || '—'}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{c.province || '—'}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{provinciaToRegione(c.province)}</td>
                    <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{fmtDate(c.passwordResetAt)}</td>
                    <td className="px-4 py-3 text-right sticky right-0 z-10 bg-white group-hover:bg-gray-50 border-l border-gray-200 relative after:content-[''] after:absolute after:top-0 after:bottom-0 after:left-0 after:-translate-x-full after:w-2 after:pointer-events-none after:[background:linear-gradient(to_left,rgba(0,0,0,0.10),rgba(0,0,0,0))]">
                      {renderClientActions(c, setActionModal)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      )}

      <FullPagination page={page} pages={pages} total={total} pageSize={PAGE_SIZE} onPageChange={setPage} testIdPrefix="clients-pagination" />

      <ConfirmActionDialog
        open={!!pendingAction}
        onOpenChange={(open) => !open && setPendingAction(null)}
        title={pendingAction?.type === 'block' ? 'Blocca Cliente' : 'Sblocca Cliente'}
        description={
          pendingAction?.type === 'block'
            ? `Sei sicuro di voler bloccare ${pendingAction?.clientName}? L'azione va effettuata sul gestionale ERP.`
            : `Sei sicuro di voler sbloccare ${pendingAction?.clientName}?`
        }
        confirmLabel={pendingAction?.type === 'block' ? 'Blocca' : 'Sblocca'}
        variant={pendingAction?.type === 'block' ? 'destructive' : 'warning'}
        onConfirm={handleConfirmAction}
      />

      <ClientCardModal open={!!actionModal} onClose={() => setActionModal(null)}>
        {actionModal?.type === 'change-email' && (
          <ChangeEmailCard clientKey={actionModal.client.an_conto ?? actionModal.client.userId ?? ''} currentEmail={actionModal.client.accountEmail || actionModal.client.email} onSuccess={closeModalAndReload} />
        )}
        {actionModal?.type === 'reset-credentials' && (
          <ResetCredentialsCard clientKey={actionModal.client.an_conto ?? actionModal.client.userId ?? ''} email={actionModal.client.accountEmail || actionModal.client.email} onSuccess={closeModalAndReload} />
        )}
        {actionModal?.type === 'create-account' && (
          <CreateAccountCard anConto={actionModal.client.an_conto} defaultEmail={actionModal.client.email} onSuccess={closeModalAndReload} />
        )}
      </ClientCardModal>
    </div>
  );
}
